<script setup lang="ts">
/**
 * Label + control + hint/error, wired for screen readers. The default slot
 * receives the ids to put on the control:
 *   <DField label="Email" :error="e" v-slot="field"><DInput v-bind="field" /></DField>
 */
import { computed, useId } from 'vue';

const props = defineProps<{
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}>();

const id = useId();
const hintId = `${id}-hint`;
const errorId = `${id}-error`;
const control = computed(() => ({
  id,
  'aria-invalid': props.error ? ('true' as const) : undefined,
  'aria-describedby': props.error ? errorId : props.hint ? hintId : undefined,
}));
</script>

<template>
  <div class="field">
    <div class="field__row">
      <label class="field__label" :for="id">{{ label }}</label>
      <slot name="aside" />
    </div>
    <slot v-bind="control" />
    <p v-if="error" :id="errorId" class="field__error" role="alert">{{ error }}</p>
    <p v-else-if="hint" :id="hintId" class="field__hint">{{ hint }}</p>
  </div>
</template>
